/**
 * Phạm vi đang xem trên trang giám sát.
 *
 * Trang có hai lớp thu hẹp lồng nhau: lọc theo đại lý/người dùng (nằm ở query string) và chọn một
 * chiếc xe (nằm ở state, đôi khi có thêm `device_id` trên URL).
 *
 * File này quyết định ĐANG Ở LỚP NÀO và LỐI RA LÀ GÌ. Tách khỏi React để test bằng jest: chỉ nhận
 * chuỗi query và object xe, không đụng DOM, không đụng router.
 *
 * Hai lớp có hai lối ra KHÁC NHAU, và chỉ lớp xe có nút:
 *   - lớp xe     -> nút "Bỏ chọn xe" trên dải phạm vi.
 *   - lớp đại lý -> bấm lại chính node đang mở trong cây: nó thu lại và màn hình lùi về ĐẠI LÝ CHA.
 * Lớp đại lý từng có nút "Xem toàn bộ" nhảy thẳng một phát về gốc. Bỏ đi vì nó nhảy qua mọi cấp
 * trung gian: từ một đại lý sâu ba tầng, người dùng muốn lùi một bậc lại bị ném về toàn đội xe.
 * Cây lùi từng bậc nên dải này chỉ còn NHÃN cho lớp đại lý — biết mình đang ở đâu, không có nút.
 */

/** Tham số định nghĩa phạm vi đại lý. `search_agency_id` hiện là bí danh của `agency_id`. */
export const SCOPE_PARAMS = ['agency_id', 'end_user_id', 'search_agency_id'];

/** Tham số ghim một chiếc xe cụ thể. */
export const VEHICLE_PARAMS = ['device_id', 'license_plate'];

/** Nhãn dùng khi mở thẳng bằng URL nên chưa biết tên đại lý. */
export const GENERIC_SCOPE_LABEL = 'Đang lọc theo đại lý';

/** Nhãn dùng khi xe không có tên lẫn biển số. */
export const GENERIC_VEHICLE_LABEL = 'Xe đang chọn';

export const isScopedSearch = search => {
  const params = new URLSearchParams(search || '');
  return SCOPE_PARAMS.some(key => Boolean(params.get(key)));
};

/**
 * Chuỗi query sau khi bỏ chọn xe: gỡ đúng lớp xe, GIỮ nguyên lớp đại lý bao ngoài.
 * Bóc từng lớp một, không nhảy thẳng về toàn bộ.
 */
export const buildDeselectVehicleSearch = search => {
  const params = new URLSearchParams(search || '');
  VEHICLE_PARAMS.forEach(key => params.delete(key));
  return params.toString();
};

/** Nhãn của node đại lý/người dùng vừa bấm trong cây. */
export const scopeLabelOf = node => {
  const name = node && typeof node.name === 'string' ? node.name.trim() : '';
  return name || GENERIC_SCOPE_LABEL;
};

/** Nhãn của chiếc xe đang chọn: tên hiển thị nếu thiết bị bật, không thì biển số, không nữa thì serial. */
export const vehicleLabelOf = vehicle => {
  if (!vehicle) return '';
  if (vehicle.is_display_name && vehicle.name) return String(vehicle.name).trim();

  const plate = vehicle.license_plate || (vehicle.vehicle && vehicle.vehicle.license_plate);
  return String(plate || vehicle.serial || GENERIC_VEHICLE_LABEL).trim() || GENERIC_VEHICLE_LABEL;
};

/**
 * Lớp phạm vi trong cùng đang áp lên màn hình, kèm chữ cho nút thoát nếu lớp đó CÓ nút.
 *
 * Chỉ trả về MỘT lớp: đang chọn xe thì lối ra là bỏ chọn xe, bỏ xong mới tới lượt đại lý. Lớp đại
 * lý trả `action` rỗng — lối ra của nó nằm trong cây (bấm lại node đang mở để lùi một bậc), dải
 * phạm vi chỉ nói cho biết đang lọc theo ai.
 */
export const scopeOf = ({ search, vehicle, agencyLabel } = {}) => {
  if (vehicle && vehicle.device_id) {
    return { kind: 'vehicle', label: vehicleLabelOf(vehicle), action: 'Bỏ chọn xe' };
  }

  if (isScopedSearch(search)) {
    return {
      kind: 'agency',
      label: agencyLabel || GENERIC_SCOPE_LABEL,
      action: ''
    };
  }

  return { kind: 'none', label: '', action: '' };
};

/**
 * Đích của cú bấm-lần-hai lên một node đang mở trong cây: LÙI ĐÚNG MỘT BẬC.
 *
 * Đây là lối ra duy nhất của lớp đại lý sau khi nút "Xem toàn bộ" bị bỏ, nên nó phải lùi từng cấp
 * chứ không nhảy về gốc. Node khách hàng cuối lùi về đại lý chứa nó; đại lý lùi về đại lý cha; đại
 * lý đỉnh cây thì hết đường lùi và về gốc (toàn bộ đội xe tài khoản được xem).
 *
 * Trả kèm `name` để dải phạm vi hiện ĐÚNG TÊN cấp vừa lùi tới, thay vì nhãn chung.
 *
 * Tách ra thành hàm thuần vì bản trước chôn logic này trong `VehicleTableTracking`: nó đọc
 * `node.parentId` mà KHÔNG chỗ nào gán trường đó, nên mọi đại lý lồng nhau đều rơi về gốc và
 * không có gì phát hiện ra. Test ở đây khoá lại từng nhánh.
 */
export const stepUpTargetOf = node => {
  if (!node) return { type: 'root' };

  if (node.type === 'end_user' && node.agencyId) {
    return { type: 'agency', id: node.agencyId, name: node.agencyName };
  }

  if (node.type === 'agency' && node.parentId) {
    return { type: 'agency', id: node.parentId, name: node.parentName };
  }

  return { type: 'root' };
};
